/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
import React from 'react';
import { Box, HStack, VStack } from 'chakra-v3';
import { Text } from './Text';
import { BsArrowUpRight } from 'react-icons/bs';

export interface CaseStudyCardProps {
  title: string;
  description: string;
  href: string;
  readMoreLabel?: string;
}

/**
 * CaseStudyCard Component
 *
 * Displays a case study with title, description, and read more link.
 * Follows the Chakra UI v3 slot recipe pattern for consistent theming.
 *
 * @example
 * ```tsx
 * <CaseStudyCard
 *   title="SAMIKSHA"
 *   description="Assessment of generative models on civic reasoning and cultural understanding."
 *   href="#"
 * />
 * ```
 */
export const CaseStudyCard: React.FC<CaseStudyCardProps> = ({
  title,
  description,
  href,
  readMoreLabel = 'Read more',
}) => {
  const handleClick = () => {
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      bg="white"
      w={{ base: '100%', lg: '304px' }}
      h={{ base: 'auto', lg: '341px' }}
      position="relative"
      p="23px"
      display="flex"
      flexDirection="column"
    >
      <VStack alignItems="flex-start" gap="8px" flex="1">
        <Text variant="label" fontSize="13px" color="primary-color" letterSpacing="1.3px" textTransform="uppercase">
          {title}
        </Text>
        <Box w="85px" h="1px" bg="primary-color" />
        <Text
          variant="inherit"
          fontSize={{ base: '20px', md: '24px' }}
          color="primary-color"
          fontFamily="'Noto Serif', serif"
          lineHeight="normal"
          letterSpacing="-0.72px"
          flex="1"
        >
          {description}
        </Text>
      </VStack>
      <HStack
        justifyContent="space-between"
        alignItems="center"
        mt="auto"
        pt="20px"
        cursor="pointer"
        _hover={{ opacity: 0.7 }}
        onClick={handleClick}
      >
        <Text fontSize="20px" fontWeight="300" color="primary-color" lineHeight="1.406">
          {readMoreLabel}
        </Text>
        <Box>
          <BsArrowUpRight size={18} />
        </Box>
      </HStack>
    </Box>
  );
};
